import type { Dispatch, ReactElement, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { QueryClient } from '@tanstack/react-query';
import {
  verifyExternalQuestsUserQuestsVerifyExternalQuestsPost,
} from '../../../client';
import type { DailyQuestAssignAndGetResponse, DailyQuestWithProgress, QuestObjective } from '../../../models/apiTypes';
import type { DailyQuestStartTarget, DailyQuestView } from '../../../models/quest';
import PlayTriangleIcon from '../../../components/PlayTriangleIcon';
import { showAlert } from '../../../utils/telegram/alert';
import { isAdQuest } from '../../../utils/quest/questHelpers';
import { cn } from '../../cn';


export interface DailyQuestCardStartActionsProps {
  quest: DailyQuestView;
  t: TFunction;
  queryClient: QueryClient;
  startedVerificationQuestIds: Set<number>;
  isVerifyingQuest: number | null;
  setIsVerifyingQuest: Dispatch<SetStateAction<number | null>>;
  setStartedVerificationQuestIds: Dispatch<SetStateAction<Set<number>>>;
  setCloudStorageItem: (key: string, value: string, callback?: (success: boolean) => void) => void;
  handleStartAction: (quest: Pick<DailyQuestView, 'questId' | 'userQuestId' | 'objectives'>) => Promise<void>;
  showingAdQuestIds: Set<number>;
  needsStartButton: (quest: DailyQuestStartTarget) => boolean;
}

export default function DailyQuestCardStartActions({
  quest,
  t,
  queryClient,
  startedVerificationQuestIds,
  isVerifyingQuest,
  setIsVerifyingQuest,
  setStartedVerificationQuestIds,
  setCloudStorageItem,
  handleStartAction,
  showingAdQuestIds,
  needsStartButton,
}: DailyQuestCardStartActionsProps): ReactElement | null {
// Спеціальна обробка для партнерського квесту (quest_id 7) — класична біла кнопка з текстом
// (як до «icon-only jump buttons»; мініап партнера відкривається з handleStartAction).
if (quest.questId === 7 && !quest.isFinished) {
  const isShowingAd = !!(quest.userQuestId && showingAdQuestIds.has(quest.userQuestId));
  const FAUCET = 'FAUCET';
  const isStarted = !!(quest.userQuestId && startedVerificationQuestIds.has(quest.userQuestId));
  const isVerifying = isVerifyingQuest === quest.userQuestId;
  const isBusy = (isShowingAd && !isStarted) || isVerifying;

  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        if (isStarted && !isVerifying) {
          if (!quest.userQuestId) return;

          setIsVerifyingQuest(quest.userQuestId);
          try {
            const { data: response } = await verifyExternalQuestsUserQuestsVerifyExternalQuestsPost({
              body: { user_quest_ids: [quest.userQuestId] },
              throwOnError: true,
            });
            const updatedQuest = response.quests.find(q => q.user_quest_id === quest.userQuestId);

            if (updatedQuest?.is_finished) {
              const questKeys = ['dailyQuests', 'hourlyQuests', 'marketingQuests'] as const;
              questKeys.forEach((key) => {
                const currentData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>([key]);
                if (!currentData?.quests) return;

                const updatedQuests = currentData.quests.map((q: DailyQuestWithProgress) => {
                  if (q.user_quest_id !== quest.userQuestId) return q;
                  return {
                    ...q,
                    objectives: updatedQuest.objectives,
                    is_finished: true,
                  };
                });

                queryClient.setQueryData<DailyQuestAssignAndGetResponse>([key], {
                  ...currentData,
                  quests: updatedQuests,
                });
              });
            } else {
              setStartedVerificationQuestIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(quest.userQuestId!);
                return newSet;
              });
              setCloudStorageItem(`quest_7_started`, 'false');
            }
          } catch (error) {
            console.error('[DailyQuestsModal] Failed to verify quest:', error);
            showAlert(t("Failed to verify quest. Please try again."));
          } finally {
            setIsVerifyingQuest(null);
          }
        } else if (!isShowingAd && !isStarted) {
          handleStartAction(quest);
        }
      }}
      disabled={isBusy}
      className={cn(
        'flex w-full items-center justify-center gap-md rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised',
        isBusy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer opacity-100',
      )}
    >
      {isVerifying
        ? t('Verifying...')
        : isStarted
          ? t('Verify')
          : t('GO TO {{faucet}}', { faucet: FAUCET })}
    </button>
  );
}

// Якщо потрібна кнопка START (не ad quest або ad quest не завершений і немає progress)
// Але не для квестів 5, 6, 106, 107, 108, 172, 208, 209, бо вони мають свої спеціальні кнопки вище
// Також не для квестів відвідування локацій, бо вони мають кнопки-переходи вище
const hasVisitObjective = quest.objectives?.some((obj: QuestObjective) => obj.type === 'visit_static_map_object');
if (needsStartButton(quest) && quest.questId !== 5 && quest.questId !== 6 && quest.questId !== 7 && quest.questId !== 106 && quest.questId !== 107 && quest.questId !== 108 && quest.questId !== 172 && quest.questId !== 208 && quest.questId !== 209 && !hasVisitObjective) {
  const isShowingAd = !!(quest.userQuestId && showingAdQuestIds.has(quest.userQuestId));
  const isAdQuestStart = isAdQuest(quest.questId, quest.objectives?.[0]?.type);
  // While the ad is being requested the button used to just dim with no
  // explanation, leaving the player in front of a dead control.
  const startLabel = isShowingAd ? t("Loading...") : isAdQuestStart ? t("Watch") : t("SHOW");
  
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (!isShowingAd) {
          handleStartAction(quest);
        }
      }}
      disabled={isShowingAd}
      className={cn(
        'flex w-full items-center justify-center rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised',
        isShowingAd ? 'cursor-not-allowed opacity-60' : 'cursor-pointer opacity-100',
      )}
    >
      <span className="inline-flex items-center gap-sm">
        <span>{startLabel}</span>
        {isAdQuestStart && !isShowingAd && <PlayTriangleIcon size={13} color={'var(--color-accent)'} />}
      </span>
    </button>
  );
}

// Інакше - показуємо кнопку claim (для звичайних квестів або завершених ad quest)
return null;
}
